import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Link,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Security as SecurityIcon,
  AssignmentInd as AssignmentIndIcon,
  Work as WorkIcon,
  TimerOff as TimerOffIcon,
  Bolt as BoltIcon,
  Link as IntegrationIcon,
  AddCircleOutline as CreateIcon,
  GroupAdd as InviteIcon,
  AccessTime as TrialIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatRelativeTime } from '../utils/dateUtils';
import { DonutChart } from '../components/Reports/charts';
import { getVendorLabel } from '../components/Integrations/agentMeta';

const EMPTY_DASHBOARD = {
  generatedAt: null,
  detections: { open: 0, bySeverity: { Critical: 0, High: 0, Medium: 0, Low: 0 }, unassigned: 0, last7d: 0, suppressed: 0 },
  intel7d: { malicious: 0, suspicious: 0, clean: 0, unknown: 0, unchecked: 0 },
  cases: { open: 0, unassigned: 0, slaBreached: 0, slaAtRisk: [], breachedList: [] },
  recentCritical: [],
  topEntities: [],
  automation: { rules: 0, enabled: 0, matches7d: 0, topRules: [] },
  integrations: [],
};

const VERDICT_COLOR = { malicious: 'error', suspicious: 'warning', clean: 'success' };

const daysUntil = (date) => (date ? Math.ceil((new Date(date).getTime() - Date.now()) / 86400000) : null);

const truncate = (str, n = 90) => {
  if (!str) return '';
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
};

const formatHHMM = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** Relative time to a future due date, e.g. "Due in 2h 15m" / "Overdue". */
const formatDueIn = (dueAt) => {
  if (!dueAt) return null;
  const diffMs = new Date(dueAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  if (diffMs <= 0) return 'Overdue';
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `Due in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `Due in ${hrs}h${remMins ? ` ${remMins}m` : ''}`;
};

export const Dashboard = () => {
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor } = useSettings();
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);

      const res = await apiService.reports.getDashboard();
      const payload = res.data || {};
      setData({
        ...EMPTY_DASHBOARD,
        ...payload,
        detections: { ...EMPTY_DASHBOARD.detections, ...payload.detections, bySeverity: { ...EMPTY_DASHBOARD.detections.bySeverity, ...payload.detections?.bySeverity } },
        intel7d: { ...EMPTY_DASHBOARD.intel7d, ...payload.intel7d },
        cases: { ...EMPTY_DASHBOARD.cases, ...payload.cases },
        automation: { ...EMPTY_DASHBOARD.automation, ...payload.automation },
        recentCritical: payload.recentCritical || [],
        topEntities: payload.topEntities || [],
        integrations: payload.integrations || [],
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const severityStyle = (severity) => {
    const color = getSeverityColor(severity) || {};
    return { backgroundColor: color.main || '#757575', color: color.contrastText || '#fff', fontWeight: 'bold' };
  };
  const statusColor = (status) => {
    const c = getStatusColor(status);
    return (typeof c === 'string' ? c : c?.main) || '#757575';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchDashboardData()}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  const company = user?.company || {};
  const subscription = company.subscription || {};
  const limits = company.limits || {};
  const usage = company.usage || {};
  const isTrial = subscription.plan === 'trial';
  const trialDaysLeft = daysUntil(subscription.trialEndsAt);
  const isBrandNew = data.integrations.length === 0 && data.detections.last7d === 0 && data.cases.open === 0 && data.topEntities.length === 0;

  const usageRows = [
    { label: 'Detections', used: usage.currentDetections || 0, max: limits.maxDetections },
    { label: 'Cases', used: usage.currentCases || 0, max: limits.maxCases },
    { label: 'Users', used: usage.currentUsers || 0, max: limits.maxUsers },
  ].filter((r) => r.max !== undefined);

  const connectedIntegrations = data.integrations.filter((a) => a.connected).length;
  const totalIntegrations = data.integrations.length;
  const integrationsColor = totalIntegrations === 0 ? 'primary' : connectedIntegrations === totalIntegrations ? 'success' : 'warning';

  const severityBreakdown = ['Critical', 'High', 'Medium', 'Low'].map((sev) => ({ sev, count: data.detections.bySeverity[sev] || 0 }));

  const intelChartData = [
    { name: 'Malicious', count: data.intel7d.malicious, key: 'malicious' },
    { name: 'Suspicious', count: data.intel7d.suspicious, key: 'suspicious' },
    { name: 'Clean', count: data.intel7d.clean, key: 'clean' },
    { name: 'Unknown', count: data.intel7d.unknown, key: 'unknown' },
    { name: 'Unchecked', count: data.intel7d.unchecked, key: 'unchecked' },
  ].filter((d) => d.count > 0);
  const intelColorFor = (d) => ({
    malicious: theme.palette.error.main,
    suspicious: theme.palette.warning.main,
    clean: theme.palette.success.main,
    unknown: theme.palette.grey[500],
    unchecked: theme.palette.text.disabled,
  }[d.key] || theme.palette.grey[500]);

  const needsAttention = data.cases.breachedList.length > 0 || data.cases.slaAtRisk.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 1 }}>
        <Typography variant="h4">Dashboard</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {data.generatedAt && (
            <Typography variant="body2" color="text.secondary">Updated {formatRelativeTime(data.generatedAt)}</Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Trial banner */}
      {isTrial && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: trialDaysLeft !== null && trialDaysLeft <= 3 ? 'error.main' : 'warning.main' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <TrialIcon color={trialDaysLeft !== null && trialDaysLeft <= 3 ? 'error' : 'warning'} sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="h6">
                    {trialDaysLeft === null
                      ? 'You are on a free trial'
                      : trialDaysLeft > 0
                        ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your trial`
                        : 'Your trial has ended'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upgrade to keep ingesting detections and unlock higher limits.
                  </Typography>
                </Box>
              </Box>
              <Button variant="contained" onClick={() => navigate('/dashboard/billing')}>View plans</Button>
            </Box>
            {usageRows.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {usageRows.map((row) => {
                  const unlimited = row.max === -1;
                  const pct = unlimited || !row.max ? 0 : Math.min(100, Math.round((row.used / row.max) * 100));
                  return (
                    <Grid item xs={12} sm={4} key={row.label}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{row.label}</Typography>
                        <Typography variant="body2" color={pct >= 90 ? 'error.main' : 'text.secondary'}>
                          {row.used} / {unlimited ? 'unlimited' : row.max}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty-state getting started */}
      {isBrandNew && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Welcome to Excubia</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nothing here yet. Get started with one of the steps below.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <IntegrationIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="subtitle1" gutterBottom>Connect an EDR</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Pull alerts automatically from CrowdStrike, SentinelOne or Microsoft Defender.
                    </Typography>
                    <Button variant="contained" size="small" component={RouterLink} to="/dashboard/integrations">Set up integration</Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <CreateIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="subtitle1" gutterBottom>Create a detection</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Paste a raw alert and let AI turn it into a structured detection.
                    </Typography>
                    <Button variant="outlined" size="small" component={RouterLink} to="/dashboard/detections">Go to Detections</Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <InviteIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="subtitle1" gutterBottom>Invite your team</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add analysts and assign roles so everyone can collaborate on cases.
                    </Typography>
                    <Button variant="outlined" size="small" component={RouterLink} to="/dashboard/settings">Manage users</Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* KPI row */}
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Open Detections"
            value={data.detections.open}
            subtitle={`${data.detections.last7d} in last 7d${data.detections.suppressed ? ` · ${data.detections.suppressed} suppressed` : ''}`}
            icon={<SecurityIcon sx={{ fontSize: 36 }} />}
            color="primary"
            to="/dashboard/detections"
            extra={(
              <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                {severityBreakdown.map(({ sev, count }) => (
                  <Chip key={sev} label={`${sev}: ${count}`} size="small" sx={severityStyle(sev)} />
                ))}
              </Box>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Unassigned Detections"
            value={data.detections.unassigned}
            subtitle={data.detections.open ? `${Math.round((data.detections.unassigned / data.detections.open) * 100)}% of open` : 'No open detections'}
            icon={<AssignmentIndIcon sx={{ fontSize: 36 }} />}
            color="warning"
            to="/dashboard/detections"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Open Cases"
            value={data.cases.open}
            subtitle={`${data.cases.unassigned} unassigned`}
            icon={<WorkIcon sx={{ fontSize: 36 }} />}
            color="info"
            to="/dashboard/cases"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="SLA Breached"
            value={data.cases.slaBreached}
            subtitle={`${data.cases.slaAtRisk.length} at risk`}
            icon={<TimerOffIcon sx={{ fontSize: 36 }} />}
            color={data.cases.slaBreached > 0 ? 'error' : 'success'}
            valueColor={data.cases.slaBreached > 0 ? 'error.main' : undefined}
            to="/dashboard/cases"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Automation (7d)"
            value={data.automation.matches7d}
            subtitle={`${data.automation.enabled}/${data.automation.rules} rules enabled`}
            icon={<BoltIcon sx={{ fontSize: 36 }} />}
            color="secondary"
            to="/dashboard/settings"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Integrations"
            value={`${connectedIntegrations}/${totalIntegrations}`}
            subtitle="connected"
            icon={<IntegrationIcon sx={{ fontSize: 36 }} />}
            color={integrationsColor}
            to="/dashboard/integrations"
          />
        </Grid>

        {/* Needs attention */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Needs attention</Typography>
              {!needsAttention ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No SLA risk right now</Typography>
              ) : (
                <>
                  {data.cases.breachedList.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                        Breached ({data.cases.breachedList.length})
                      </Typography>
                      <List disablePadding dense>
                        {data.cases.breachedList.map((c, index) => (
                          <React.Fragment key={c._id}>
                            <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/cases/${c._id}`)}>
                              <ListItemIcon>
                                <Chip label={c.severity} size="small" sx={severityStyle(c.severity)} />
                              </ListItemIcon>
                              <ListItemText
                                primary={c.caseNumber ? `${c.caseNumber} · ${c.title}` : c.title}
                                secondary="SLA breached"
                                secondaryTypographyProps={{ sx: { color: 'error.main' } }}
                              />
                            </ListItem>
                            {index < data.cases.breachedList.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </>
                  )}

                  {data.cases.slaAtRisk.length > 0 && (
                    <>
                      {data.cases.breachedList.length > 0 && <Divider sx={{ my: 1.5 }} />}
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        At risk (due within 4h)
                      </Typography>
                      <List disablePadding dense>
                        {data.cases.slaAtRisk.map((c, index) => (
                          <React.Fragment key={c._id}>
                            <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/cases/${c._id}`)}>
                              <ListItemIcon>
                                <Chip label={c.severity} size="small" sx={severityStyle(c.severity)} />
                              </ListItemIcon>
                              <ListItemText
                                primary={c.caseNumber ? `${c.caseNumber} · ${c.title}` : c.title}
                                secondary={formatDueIn(c.dueAt)}
                                secondaryTypographyProps={{ sx: { color: 'warning.main' } }}
                              />
                            </ListItem>
                            {index < data.cases.slaAtRisk.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent critical & high detections */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" gutterBottom>Recent critical &amp; high detections</Typography>
                <Link component={RouterLink} to="/dashboard/detections" underline="hover" variant="body2">View all</Link>
              </Box>
              {data.recentCritical.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No critical or high severity detections in the last 7 days.
                </Typography>
              ) : (
                <List disablePadding>
                  {data.recentCritical.map((detection, index) => (
                    <React.Fragment key={detection._id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/detections/${detection._id}`)}>
                        <ListItemIcon>
                          <Chip label={detection.severity} size="small" sx={severityStyle(detection.severity)} />
                        </ListItemIcon>
                        <ListItemText
                          primary={detection.title}
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="caption" sx={{ color: statusColor(detection.status), fontWeight: 500 }}>
                                {detection.status}
                              </Typography>
                              {detection.host && (
                                <Typography variant="caption" color="text.secondary">{detection.host}</Typography>
                              )}
                              {detection.source && (
                                <Typography variant="caption" color="text.secondary">via {detection.source}</Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeTime(detection.createdAt)}
                              </Typography>
                              {detection.verdict && VERDICT_COLOR[detection.verdict] && (
                                <Chip label={detection.verdict} size="small" color={VERDICT_COLOR[detection.verdict]} variant="outlined" />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < data.recentCritical.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Threat intel */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Threat intel (last 7 days)</Typography>
              {intelChartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No enrichment data in the last 7 days.
                </Typography>
              ) : (
                <DonutChart data={intelChartData} colorFor={intelColorFor} ariaLabel="Threat intel verdicts, last 7 days" />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Highest risk assets */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" gutterBottom>Highest-risk assets</Typography>
                <Link component={RouterLink} to="/dashboard/entities" underline="hover" variant="body2">View all</Link>
              </Box>
              {data.topEntities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No entities with risk score yet.</Typography>
              ) : (
                <List disablePadding dense>
                  {data.topEntities.map((entity, index) => (
                    <React.Fragment key={entity._id}>
                      <ListItem sx={{ px: 0, display: 'block', cursor: 'pointer' }} onClick={() => navigate(`/dashboard/entities/${entity._id}`)}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} gap={1}>
                          <ListItemText
                            sx={{ my: 0 }}
                            primary={entity.name}
                            secondary={[entity.type, entity.detections ? `${entity.detections} detections (30d)` : null].filter(Boolean).join(' · ')}
                          />
                          <Chip label={`Risk ${entity.riskScore ?? '-'}`} size="small" color="error" variant="outlined" />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.max(0, entity.riskScore || 0))}
                          sx={{ height: 6, borderRadius: 3 }}
                          color={entity.riskScore >= 75 ? 'error' : entity.riskScore >= 50 ? 'warning' : 'primary'}
                        />
                      </ListItem>
                      {index < data.topEntities.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Integrations health */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" gutterBottom>Integrations health</Typography>
                <Link component={RouterLink} to="/dashboard/integrations" underline="hover" variant="body2">Manage</Link>
              </Box>
              {data.integrations.length === 0 ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No EDR agents connected yet.</Typography>
                  <Button variant="contained" size="small" component={RouterLink} to="/dashboard/integrations">Connect an EDR</Button>
                </Box>
              ) : (
                <List disablePadding dense>
                  {data.integrations.map((agent, index) => (
                    <React.Fragment key={agent.id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate('/dashboard/integrations')} alignItems="flex-start">
                        <ListItemText
                          primary={(
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{agent.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{getVendorLabel(agent.type)}</Typography>
                            </Box>
                          )}
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={(
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Last ingestion {agent.lastIngestion ? formatRelativeTime(agent.lastIngestion) : 'never'}
                              </Typography>
                              {agent.lastError && (
                                <Typography variant="caption" color="error.main" display="block">{truncate(agent.lastError)}</Typography>
                              )}
                            </Box>
                          )}
                        />
                        <Box sx={{ ml: 1, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Chip
                            label={!agent.enabled ? 'Disabled' : agent.connected ? 'Connected' : 'Disconnected'}
                            size="small"
                            color={!agent.enabled ? 'default' : agent.connected ? 'success' : 'error'}
                            variant={!agent.enabled ? 'outlined' : 'filled'}
                          />
                          {agent.rateLimitedUntil && (
                            <Tooltip title={`Rate limited until ${formatHHMM(agent.rateLimitedUntil)}`}>
                              <Chip label="Rate limited" size="small" color="warning" variant="outlined" />
                            </Tooltip>
                          )}
                        </Box>
                      </ListItem>
                      {index < data.integrations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Automation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" gutterBottom>Automation</Typography>
                <Link component={RouterLink} to="/dashboard/settings" underline="hover" variant="body2">Manage rules</Link>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {data.automation.enabled} of {data.automation.rules} rule{data.automation.rules === 1 ? '' : 's'} enabled
                {' · '}
                {data.automation.matches7d} match{data.automation.matches7d === 1 ? '' : 'es'} in the last 7 days
              </Typography>
              {data.automation.topRules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No rule activity yet.</Typography>
              ) : (
                <List disablePadding dense>
                  {data.automation.topRules.map((rule, index) => (
                    <React.Fragment key={rule._id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={rule.name}
                          secondary={rule.lastMatchedAt ? `Last matched ${formatRelativeTime(rule.lastMatchedAt)}` : 'No recent matches'}
                        />
                        <Chip label={`${rule.matches} match${rule.matches === 1 ? '' : 'es'}`} size="small" variant="outlined" />
                      </ListItem>
                      {index < data.automation.topRules.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const StatCard = ({ title, value, subtitle, icon, color = 'primary', to, extra, valueColor }) => {
  const navigate = useNavigate();
  return (
    <Card sx={{ height: '100%', cursor: to ? 'pointer' : 'default' }} onClick={() => to && navigate(to)}>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography color="textSecondary" gutterBottom variant="body2">{title}</Typography>
            <Typography variant="h4" component="div" sx={valueColor ? { color: valueColor } : undefined}>{value}</Typography>
            {subtitle && <Typography variant="body2" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ color: `${color}.main`, flexShrink: 0 }}>{icon}</Box>
        </Box>
        {extra}
      </CardContent>
    </Card>
  );
};

export default Dashboard;
